// FAMILY-EXT: feature header redesign — выезжающая панель выбора проекта и
// доски. Делится на две колонки: слева список проектов с маленькой
// аватаркой (cover background image), справа — доски выбранного проекта.
// Открывается по тапу на наименование проекта в шапке.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';
import { usePopup } from '../../lib/popup';

import Paths from '../../constants/Paths';
import { ProjectBackgroundTypes } from '../../constants/Enums';
import BoardAddStep from '../Boards/AddStep';

import styles from './ProjectBoardPicker.module.scss';

const ProjectBoardPicker = React.memo(
  ({
    items,
    currentProjectId,
    currentBoardId,
    canAddProject,
    boardCreateError,
    onAddProject,
    onAddBoard,
    onSelect,
  }) => {
    const [selectedProjectId, setSelectedProjectId] = useState(
      currentProjectId || (items[0] && items[0].id),
    );

    const selectedProject = items.find((p) => p.id === selectedProjectId);

    const BoardAddPopup = usePopup(BoardAddStep);

    // FAMILY-EXT: панель не закрываем сразу — при отказе сервера здесь
    // покажется сообщение об ошибке. При успехе сага уводит на новую доску,
    // и панель закрывается по смене currentBoardId (эффект ниже).
    const handleBoardCreate = useCallback(
      (data) => {
        if (!selectedProject) return;
        onAddBoard(selectedProject.id, data);
      },
      [onAddBoard, selectedProject],
    );

    const prevBoardIdRef = useRef(currentBoardId);

    useEffect(() => {
      if (currentBoardId && currentBoardId !== prevBoardIdRef.current && onSelect) {
        onSelect();
      }

      prevBoardIdRef.current = currentBoardId;
    }, [currentBoardId, onSelect]);

    const handleAddProjectClick = useCallback(() => {
      if (onAddProject) onAddProject();
      if (onSelect) onSelect();
    }, [onAddProject, onSelect]);

    return (
      <div className={styles.wrapper}>
        <div className={styles.projectsColumn}>
          <ul className={styles.projects}>
            {items.map((project) => {
              const isImageBg =
                project.background &&
                project.background.type === ProjectBackgroundTypes.IMAGE &&
                project.backgroundImage &&
                project.backgroundImage.coverUrl;
              return (
                <li
                  key={project.id}
                  className={classNames(
                    styles.projectItem,
                    project.id === selectedProjectId && styles.projectItemActive,
                  )}
                >
                  <button
                    type="button"
                    className={styles.projectButton}
                    onClick={() => setSelectedProjectId(project.id)}
                  >
                    <span
                      className={styles.projectAvatar}
                      style={
                        isImageBg
                          ? {
                              backgroundImage: `url("${project.backgroundImage.coverUrl}")`,
                            }
                          : undefined
                      }
                    >
                      {!isImageBg && project.name && project.name.charAt(0).toUpperCase()}
                    </span>
                    <span className={styles.projectName}>{project.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {canAddProject && (
            <button
              type="button"
              className={styles.addProjectButton}
              onClick={handleAddProjectClick}
            >
              <Icon fitted name="plus" />
              <span>Добавить проект</span>
            </button>
          )}
        </div>

        <div className={styles.boardsColumn}>
          <div className={styles.boards}>
            {selectedProject && selectedProject.boards.length === 0 && (
              <div className={styles.boardsEmpty}>Нет доступных досок</div>
            )}
            {selectedProject &&
              selectedProject.boards.map((board) => (
                <Link
                  key={board.id}
                  to={Paths.BOARDS.replace(':id', board.id)}
                  className={classNames(
                    styles.boardLink,
                    board.id === currentBoardId && styles.boardLinkActive,
                  )}
                  onClick={onSelect}
                >
                  {board.name}
                </Link>
              ))}
            {selectedProject && (
              <BoardAddPopup onCreate={handleBoardCreate}>
                <button type="button" className={styles.addBoardButton}>
                  <Icon fitted name="plus" />
                  <span>Добавить доску</span>
                </button>
              </BoardAddPopup>
            )}
            {boardCreateError && (
              <div className={styles.boardsError}>
                {boardCreateError.code === 'E_NOT_FOUND' ||
                boardCreateError.code === 'E_FORBIDDEN'
                  ? 'Недостаточно прав: доски создаёт менеджер проекта'
                  : 'Не удалось создать доску, попробуйте ещё раз'}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

ProjectBoardPicker.propTypes = {
  items: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  currentProjectId: PropTypes.string,
  currentBoardId: PropTypes.string,
  canAddProject: PropTypes.bool,
  boardCreateError: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onAddProject: PropTypes.func,
  onAddBoard: PropTypes.func,
  onSelect: PropTypes.func,
};

ProjectBoardPicker.defaultProps = {
  currentProjectId: undefined,
  currentBoardId: undefined,
  canAddProject: false,
  boardCreateError: undefined,
  onAddProject: undefined,
  onAddBoard: undefined,
  onSelect: undefined,
};

export default ProjectBoardPicker;
